import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box, Typography, makeStyles, Grid } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";

import { createClient, updateClientProfile } from "store/slices/client";
import { CLIENT_TYPE } from "config/AppConfig";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import InputAutocompleteAddress from "components/Input/InputAutocompleteAddress";
import CustomDatePicker from "components/Input/CustomDatePicker";
import { YupValidations } from "utils/validate";
import { showToast } from "store/slices/toast";
import moment from "moment";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 15,
    marginBottom: 16,
  },
  subContactTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 12,
    marginTop: 8,
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 20,
  }
}));

const SERVICE_TYPE_OPTIONS = ["SIL", "MTA", "IN_HOME_SUPPORT", "COMMUNITY_ACCESS", "OTHER"];

const ParticipantUserProfile = ({ clientDetail, loadClientDetail, companyId, isCreate = false }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [latLng, setLatLng] = useState({});

  // Address validation state
  const [validAddress, setValidAddress] = useState(true);

  // Ref to hold validateForm from Formik render prop
  const validateFormRef = useRef(null);

  // Handle address change (user types without selecting suggestion)
  const handleAddressChange = (value = '') => {
    setValidAddress(value.trim() === clientDetail?.address1);
  };


  const {
    id = null,
    clientType = CLIENT_TYPE.PARTICIPANT,
    firstName = "",
    lastName = "",
    personalInfoEmail = "",
    recipientEmail = "",
    recipientType = "",
    dateOfBirth = "",
    address1 = "",
    postalCode = "",
    ndisPlanNumber = "",
    planStartDate = "",
    planEndDate = "",
    billingDetail = "",
    houseName = "",
    silLevel = "",
    sil_level = "",
    serviceType = "",
    emergencyContactResponse = {},
    supportCoordinatorResponse = {},
    gpResponse = {},
    planManagerContactName = "",
    planManagerOrganizationName = "",
    planManagerPhone = "",
    planManagerEmail = "",
    carePlanResponse = {},
    billingResponse = {},
    lat,
    lng,
  } = clientDetail || {};

  useEffect(() => {
    if (lat && lng) {
      setLatLng({ lat, lng });
    }
  }, [lat, lng]);
  // Reset address validation when clientDetail changes
  useEffect(() => {
    setValidAddress(true);
  }, [clientDetail?.address1]);

  // Force Formik re-validation when validAddress changes
  // This is the KEY fix: Yup .test() only runs when Formik validates,
  // so we must manually trigger validation when external state changes
  useEffect(() => {
    if (validateFormRef.current) {
      validateFormRef.current();
    }
  }, [validAddress]);

  const baseSchema = {
    recipientType: YupValidations.requiredString,
    firstName: YupValidations.requiredString,
    lastName: YupValidations.requiredString,
    recipientEmail: YupValidations.requiredString.email("validation.email.invalid"),
    personalInfoEmail: YupValidations.requiredString.email("validation.email.invalid"),
    dateOfBirth: Yup.date().nullable().max(new Date(), "validation.dateOfBirthInvalid"),
  };

  const fullSchema = {
    ...baseSchema,
    clientType: YupValidations.requiredString,
    // dateOfBirth: YupValidations.requiredString,
    ndisPlanNumber: YupValidations.requiredString,
    address: YupValidations.requiredString.test(
      'address-suggestion-required',
      'validation.address.must_select_suggestion',
      () => validAddress
    ),
    postalCode: YupValidations.requiredString,
    
    // serviceType: YupValidations.requiredString,
    // silLevel: YupValidations.requiredString,
    
    // emergencyContactFirstName: YupValidations.requiredString,
    // emergencyContactLastName: YupValidations.requiredString,
    // emergencyContactRelationship: YupValidations.requiredString,
    emergencyContactEmail: Yup.string().trim().email("validation.email.invalid"),
    // emergencyContactPhone: YupValidations.requiredString,
    
    // supportCoordinatorFirstName: YupValidations.requiredString,
    // supportCoordinatorLastName: YupValidations.requiredString,
    // supportCoordinatorOrganizationName: YupValidations.requiredString,
    supportCoordinatorEmail: Yup.string().trim().email("validation.email.invalid"),
    // supportCoordinatorMobile: YupValidations.requiredString,
    
    // gpDoctorFirstName: YupValidations.requiredString,
    // gpDoctorLastName: YupValidations.requiredString,
    // gpPracticeName: YupValidations.requiredString,
    // gpDoctorPhone: YupValidations.requiredString,
    
    // planManagerOrganizationName: YupValidations.requiredString,
    // planManagerContactName: YupValidations.requiredString,
    planManagerEmail: YupValidations.requiredString.email("validation.email.invalid"),
    // planManagerMobile: YupValidations.requiredString,
    
    // carePlanPrimaryDiagnosis: YupValidations.requiredString,
    // carePlanSecondaryConditions: YupValidations.requiredString,
    // carePlanPersonalCare: YupValidations.requiredString,
    // carePlanMobility: YupValidations.requiredString,
    // carePlanMedicationManagement: YupValidations.requiredString,
    // carePlanCommunityAccess: YupValidations.requiredString,

    billingType: YupValidations.requiredString,
    // billingContactName: YupValidations.requiredString,
    // billingAddress: YupValidations.requiredString,
    billingEmail: YupValidations.requiredString.email("validation.email.invalid"),
    // billingPhone: YupValidations.requiredString,
  };

  const validationSchema = Yup.object().shape(isCreate ? baseSchema : fullSchema);

  const initForm = {
    clientType: clientType || CLIENT_TYPE.PARTICIPANT,
    personalInfoEmail: personalInfoEmail || "",
    recipientType: recipientType,
    firstName: firstName || "",
    lastName: lastName || "",
    recipientEmail: recipientEmail || "",
    dateOfBirth: dateOfBirth || "",
    address: address1 || "",
    postalCode: postalCode || "",
    ndisPlanNumber: ndisPlanNumber || "",
    planStartDate: planStartDate || "",
    planEndDate: planEndDate || "",
    houseName: houseName || "",
    silLevel: silLevel || sil_level || "",
    serviceType: serviceType || "",
    
    emergencyContactFirstName: emergencyContactResponse?.firstName || "",
    emergencyContactLastName: emergencyContactResponse?.lastName || "",
    emergencyContactRelationship: emergencyContactResponse?.relationship || "",
    emergencyContactPhone: emergencyContactResponse?.phone || "",
    emergencyContactEmail: emergencyContactResponse?.email || "",
    
    supportCoordinatorFirstName: supportCoordinatorResponse?.firstName || "",
    supportCoordinatorLastName: supportCoordinatorResponse?.lastName || "",
    supportCoordinatorOrganizationName: supportCoordinatorResponse?.organizationName || "",
    supportCoordinatorMobile: supportCoordinatorResponse?.phone || "",
    supportCoordinatorEmail: supportCoordinatorResponse?.email || "",
    
    gpDoctorFirstName: gpResponse?.firstName || "",
    gpDoctorLastName: gpResponse?.lastName || "",
    gpPracticeName: gpResponse?.practiceName || "",
    gpDoctorPhone: gpResponse?.phone || "",
    gpAddress: gpResponse?.address || "",
    
    planManagerContactName: planManagerContactName || "",
    planManagerOrganizationName: planManagerOrganizationName || "",
    planManagerMobile: planManagerPhone || "",
    planManagerEmail: planManagerEmail || "",
    
    carePlanPrimaryDiagnosis: carePlanResponse?.primaryDiagnosis || "",
    carePlanSecondaryConditions: carePlanResponse?.secondaryConditions || "",
    carePlanPersonalCare: carePlanResponse?.personalCare || "",
    carePlanMedicationManagement: carePlanResponse?.medicationManagement || "",
    carePlanCommunityAccess: carePlanResponse?.communityAccess || "",
    carePlanMobility: carePlanResponse?.mobility || "",
    carePlanCommunicationStrategy: carePlanResponse?.communicationStrategy || "",
    carePlanGoals: carePlanResponse?.goals || "",
    
    billingType: billingDetail || "",
    billingOrganizationName: billingResponse?.organizationName || "",
    billingContactName: billingResponse?.contactName || "",
    billingAddress: billingResponse?.address || "",
    billingEmail: billingResponse?.email || "",
    billingPhone: billingResponse?.phone || "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = { 
        ...values, 
        companyId,
        lat: latLng?.lat,
        lng: latLng?.lng
      };

      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null) {
          delete payload[key];
        }
      });

      let result;
      if (id) {
        payload.clientId = id;
        result = await dispatch(updateClientProfile(payload));
      } else {
        result = await dispatch(createClient(payload));
      }

      if (id ? updateClientProfile.fulfilled.match(result) : createClient.fulfilled.match(result)) {
        if (loadClientDetail) {
          loadClientDetail();
        } else {
          history.push(`/admin/participants/${result.payload.id}`);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const recipientTypeOptions = [
    { value: "SUPPORT_COORDINATOR", label: t("recipient_type.SUPPORT_COORDINATOR") },
    { value: "FAMILY_MEMBER_OR_GUARDIAN", label: t("recipient_type.FAMILY_MEMBER_OR_GUARDIAN") },
    { value: "NOMINEE", label: t("recipient_type.NOMINEE") },
    { value: "PARTICIPANT", label: t("recipient_type.PARTICIPANT") },
  ];

  const serviceTypeOptionsList = SERVICE_TYPE_OPTIONS.map((v) => ({ value: v, label: t(`service_type.${v}`) }));
  const silLevelOptions = ["STANDARD", "HIGH", "COMPLEX", "RESPITE"].map((v) => ({
    value: v, label: t(`sil_level.${v}`),
  }));
  const personalCareOptions = ["FULL_ASSIST", "PROMPTING_PHYSICAL_ASSIST", "PROMPTING_ONLY", "SUPERVISED_ONLY", "INDEPENDENT"].map((v) => ({
    value: v, label: t(`personal_care.${v}`),
  }));
  const mobilityOptions = ["AMBULANT_NO_AIDS", "WALKING_FRAME", "MANUAL_WHEELCHAIR", "POWERED_WHEELCHAIR", "HOIST_REQUIRED", "AMBULANT_SUPERVISION"].map((v) => ({
    value: v, label: t(`mobility.${v}`),
  }));
  const medicationManagementOptions = ["WORKER_ADMINISTERS", "SUPERVISED_SELF_ADMIN", "INDEPENDENT", "PRN_ONLY"].map((v) => ({
    value: v, label: t(`medication_management.${v}`),
  }));
  const communityAccessOptions = ["RATIO_1_1_ALL_TIMES", "RATIO_1_1_FAMILIAR_ONLY", "RATIO_1_2", "SUPERVISED_GROUP", "INDEPENDENT_FAMILIAR"].map((v) => ({
    value: v, label: t(`community_access.${v}`),
  }));
  const billingTypeOptions = ["SELF", "PLAN_MANAGER", "NDIA"].map((v) => ({
    value: v, label: t(`billing_type.${v}`),
  }));
  
  const fullName = id ? `${firstName} ${lastName}`.trim() : null;

  return (
    <Box>
      <Formik
        enableReinitialize={true}
        initialValues={initForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values, touched, errors, validateForm }) => {
          // Store validateForm in ref so useEffect can call it
          validateFormRef.current = validateForm;
          return (
          <Form>
            {/* Part 1 - Personal Information */}
            <Typography className={classes.sectionTitle}>
              {t("title.personalInformation")}
            </Typography>
             <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="personalInfoEmail" label="label.email" required />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="firstName" label="label.firstName" required />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="lastName" label="label.lastName" required />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="recipientEmail" label="label.recipientEmail" required />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="recipientType" label="label.recipient_type" options={recipientTypeOptions} required />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={CustomDatePicker}
                  name="dateOfBirth"
                  type="text"
                  id="dateOfBirth"
                  label={"label.dateOfBirth"}
                  classInput={'date-picker-container-custom'}
                  formatDateJob={true}
                  custom={{
                    openTo: "year",
                    views: ["year", "month", "date"],
                    maxDate: new Date(moment(new Date().setDate(new Date().getDate() - 1)))
                  }}
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="ndisPlanNumber" label="label.ndisPlanNumber" required={!isCreate} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={InputAutocompleteAddress}
                  name="address"
                  label={t("label.address")}
                  onChangePostalCode={(postalcode) => {
                    setFieldValue("postalCode", postalcode);
                  }}
                  onChangeLatLng={(latlng) => {
                    setValidAddress(true);
                    setLatLng(latlng);
                  }}
                  setValidAddress={setValidAddress}
                  allowPattern={true}
                  onAddressChange={handleAddressChange}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="postalCode" label="label.postalCode" required={!isCreate} />
              </div>
            </div>

            {/* Part 2 - NDIS Plan Details */}
            <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
              {t("label.ndis_plan_details")}
            </Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={CustomDatePicker} name="planStartDate" label="label.startDate"
                  onChange={(date) => setFieldValue("planStartDate", date)} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={CustomDatePicker} name="planEndDate" label="label.endDate"
                  onChange={(date) => setFieldValue("planEndDate", date)} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="serviceType" label="label.serviceType" options={serviceTypeOptionsList} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="silLevel" label="label.silLevel" options={silLevelOptions} />
              </div>
              {values.serviceType === "SIL" && (
                <div className="col-sm-12 col-md-6">
                  <Field component={AppInput} name="houseName" label="label.house_assignment" />
                </div>
              )}
            </div>

            {/* Part 3 - Contacts */}
            <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
              {t("label.contacts")}
            </Typography>

            {/* Emergency Contact */}
            <Typography className={classes.subContactTitle}>{t("label.emergency_contact")}</Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="emergencyContactFirstName" label="label.firstName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="emergencyContactLastName" label="label.lastName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="emergencyContactRelationship" label="label.relationship" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="emergencyContactEmail" label="label.email" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="emergencyContactPhone" label="label.phone" />
              </div>
            </div>

            {/* Support Coordinator */}
            <Typography className={classes.subContactTitle}>{t("label.support_coordinator")}</Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="supportCoordinatorFirstName" label="label.firstName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="supportCoordinatorLastName" label="label.lastName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="supportCoordinatorOrganizationName" label="label.organizationName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="supportCoordinatorEmail" label="label.email" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="supportCoordinatorMobile" label="label.phone" />
              </div>
            </div>

            {/* GP */}
            <Typography className={classes.subContactTitle}>GP</Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="gpDoctorFirstName" label="label.firstName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="gpDoctorLastName" label="label.lastName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="gpPracticeName" label="label.practice_name" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="gpDoctorPhone" label="label.phone" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="gpAddress" label="label.address" />
              </div>
            </div>

            {/* Plan Manager */}
            <Typography className={classes.subContactTitle}>{t("label.plan_manager")}</Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="planManagerContactName" label="label.contact_name" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="planManagerOrganizationName" label="label.organizationName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="planManagerMobile" label="label.phone" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="planManagerEmail" label="label.email" required={!isCreate} />
              </div>
            </div>

            {/* Part 4 - Care Plan Details */}
            <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
              {t("label.care_plan_details")}
            </Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="carePlanPrimaryDiagnosis" label="label.primary_diagnosis" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="carePlanPersonalCare" label="label.personal_care" options={personalCareOptions} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="carePlanMedicationManagement" label="label.medication_management" options={medicationManagementOptions} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="carePlanCommunityAccess" label="label.community_access" options={communityAccessOptions} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="carePlanMobility" label="label.mobility" options={mobilityOptions} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="carePlanCommunicationStrategy" label="label.communication_strategy" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} type="textarea" name="carePlanSecondaryConditions" label="label.secondary_conditions" />
              </div>
              {
                fullName &&
                <div className="col-sm-12 col-md-6">
                  <Field component={AppInput} type="textarea" name="carePlanGoals" label={t("label.carePlanGoals", { value: fullName })} />
                </div>
              }
            </div>

            {/* Part 5 - Billing Details */}
            <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
              {t("label.billingDetails")}
            </Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field component={SelectField} name="billingType" label="label.billing_type" options={billingTypeOptions} required={!isCreate} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="billingOrganizationName" label="label.organizationName" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="billingContactName" label="label.contact_name" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="billingAddress" label="label.address" />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="billingEmail" label="label.email" required={!isCreate} />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field component={AppInput} name="billingPhone" label="label.phone" />
              </div>
            </div>

            {/* Actions */}
            <Box className={classes.control}>
              <AppButton
                label="button.cancel"
                buttonColor="#9CA3AF"
                onClick={() => history.goBack()}
                isLoading={isSubmitting}
                disabled={isSubmitting}
                style={{ marginRight: 16 }}
              />
              <AppButton
                label="button.save_changes"
                buttonColor="#2dd4bf"
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              />
            </Box>
          </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

ParticipantUserProfile.propTypes = {
  clientDetail: PropTypes.object,
  loadClientDetail: PropTypes.func,
  companyId: PropTypes.number,
  isCreate: PropTypes.bool,
};

export default ParticipantUserProfile;
